import { useCallback, useEffect, useState } from "react";
import {
  EncryptionWarning,
  SettingField,
  TestButton,
  type SettingFieldData,
} from "../components/SettingField";
import { useI18n, serverError } from "../i18n";

interface AiSettingsPayload {
  encryptionReady: boolean;
  /** 沒綁定時實際會用到的候選清單，依序 */
  fallbackModels: string[];
  /** 可以直接點選的 Workers AI 模型。inK/outK 是每百萬 token 的 neurons */
  workersAiCatalog: { id: string; inK: number | null; outK: number | null }[];
  /** 綁了金鑰但沒填模型時用哪一個 */
  defaultOpenRouterModel: string;
  fields: SettingFieldData[];
}

/**
 * AI 供應商
 * ---------------------------------------------------------------
 *
 * ── 為什麼需要這一頁 ────────────────────────────────────────
 *
 * 離線代理人第一次上線就整個不動，原因是它用的
 * @cf/meta/llama-3.1-8b-instruct 被下架了。模型名稱是一個字串，
 * 而我們對那個生命週期沒有任何控制權 —— 沒有型別、沒有測試、
 * 沒有部署檢查抓得出「這個名字已經不存在了」。
 *
 * 候選清單讓「其中一個下架」不再致命，但整個平台仍然綁在一家
 * 供應商上。OpenRouter 後面接的是幾十家，綁了它之後換模型變成
 * 在這一頁改一行字 —— 不用重新部署，也不用等我們發版。
 *
 * ── 這一頁可以永遠是空的 ────────────────────────────────────
 *
 * 沒填就走下面列出的 Workers AI 候選清單，功能完全正常。
 * 綁定是一個選項，不是前提 —— 所以這一頁的預設狀態必須看起來
 * 像「一切正常」，而不是像「你還沒設定」。
 */
export default function SettingsAi() {
  const { t } = useI18n();
  const [data, setData] = useState<AiSettingsPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch("/api/settings/ai", { credentials: "same-origin" });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(serverError(payload, t, `HTTP ${res.status}`));
      setData(payload.data as AiSettingsPayload);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <p className="rounded-card nm-inset p-6 text-sm text-brand-300">{error}</p>;
  if (!data) return <div className="h-80 rounded-card shimmer" />;

  const field = (key: string) => data.fields.find((f) => f.key === key);
  /*
    ── 這裡原本有一塊「目前在跑什麼」的狀態框 ──────────────────

    它把兩個區塊的設定合起來算出最終的嘗試順序。想法是對的，
    但它和下面兩個欄位講的是同一件事 —— 欄位本身已經有狀態標籤、
    有目前的值，而候選晶片會把生效中的那一個標起來。

    同一件事在一頁上說兩次，讀的人要先確認兩邊有沒有衝突才敢相信
    任何一邊。拿掉。
  */

  return (
    <div className="max-w-2xl space-y-5">
      {!data.encryptionReady && <EncryptionWarning service="messaging" />}

      <section className="rounded-card nm p-5">
        <h2 className="text-sm font-semibold text-ink">OpenRouter</h2>
        <p className="mt-1.5 text-xs leading-relaxed text-ink-muted">
          {t("sa.lede")}{" "}
          <a
            href="https://openrouter.ai/keys"
            target="_blank"
            rel="noreferrer noopener"
            className="text-brand-300 underline underline-offset-2"
          >
            openrouter.ai/keys
          </a>{" "}
          {t("sa.ledeTail")}
        </p>

        <div className="mt-4 space-y-3">
          {field("openrouter_api_key") && (
            <SettingField
              field={{ ...field("openrouter_api_key")!, secret: true }}
              endpoint="/settings/ai"
              placeholder="sk-or-v1-..."
              hint={t("sa.keyHint")}
              revocable
              onSaved={load}
            />
          )}

          {field("openrouter_model") && (
            <SettingField
              field={field("openrouter_model")!}
              endpoint="/settings/ai"
              placeholder={data.defaultOpenRouterModel}
              hint={t("sa.modelHint", { fallback: data.defaultOpenRouterModel })}
              revocable
              onSaved={load}
            />
          )}
        </div>

        <TestButton endpoint="/settings/ai/test" label={t("sa.test")} />
        <p className="mt-2 text-[11px] leading-relaxed text-ink-muted">{t("sa.testNote")}</p>
      </section>

      {/*
        Workers AI 自己的模型也要能換。

        這整件事的起因就是一個寫死的模型名稱被下架 ——
        把「我們自己的那一個」也留在程式碼裡，等於同一個坑挖了兩次。
      */}
      <section className="rounded-card nm p-5">
        <h2 className="text-sm font-semibold text-ink">Workers AI</h2>
        <p className="mt-1.5 text-xs leading-relaxed text-ink-muted">
          {t("sa.cfLede")}{" "}
          <a
            href="https://developers.cloudflare.com/workers-ai/models/"
            target="_blank"
            rel="noreferrer noopener"
            className="text-brand-300 underline underline-offset-2"
          >
            developers.cloudflare.com/workers-ai/models
          </a>
        </p>

        <p className="mt-2 text-[11px] leading-relaxed text-ink-muted">{t("sa.cfFreeNote")}</p>

        <div className="mt-4">
          {field("workers_ai_model") && (
            <SettingField
              field={field("workers_ai_model")!}
              endpoint="/settings/ai"
              placeholder={data.fallbackModels[0]}
              hint={t("sa.cfModelHint")}
              suggestions={data.workersAiCatalog.map((m) => ({
                value: m.id,
                /*
                  標成本，不標「免費」。

                  Workers AI 沒有免費的模型 —— 它給的是每天一萬
                  neurons 的共用額度。所以要看的不是哪一個免費，
                  是同樣的額度哪一個撐得久，而那個差距有十倍以上。
                */
                note: m.inK ? `${(m.inK / 1000).toFixed(1)}k` : undefined,
              }))}
              revocable
              onSaved={load}
            />
          )}
        </div>
      </section>

      {/*
        退回機制要寫出來。

        悄悄降級和悄悄失敗一樣糟：帳單上看得到 Workers AI 的用量，
        而管理員以為自己在用 OpenRouter。
      */}
      <p className="text-[11px] leading-relaxed text-ink-muted">{t("sa.fallbackNote")}</p>
    </div>
  );
}
